import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import { NextSeo } from 'next-seo';

import { adWriteApi } from '../../../api/adWriteApi';
import { carValuationApi } from '../../../api/carValuationApi';
import { userRepository } from '../../../repositories/User/UserRepository';
import { CarValuationResponse } from '../../../api/types/CarValuationApiTypes';

import { CarValuationPage } from '../../../components/CarValuationPage/CarValuation';
import { CarValuationErrorPage } from '../../../components/CarValuationPage/CarValuationErrorPage';

import { DefaultLayout } from '../../../components/Layouts';
import { IMetaInfoItem } from '../../../components/Toolkit/PublishFilters/types/index';
import { TCarValuationPageNames, PAGE } from '../../../helpers/pages';
import { getServerSession } from 'next-auth';
import { nextAuthOptions } from '../../../helpers/nextAuthOptions';
import { formatAuthorizationHeader } from '../../../helpers/auth';
import { isOk } from '../../../domains/Result';
import { logger } from '@dist-marketplace-frontend/services';
import { VERTICAL } from '../../../helpers/verticals';
import { useEffect } from 'react';
import { useVerticalContext } from '../../../contexts/VerticalContext';

const {
  publicRuntimeConfig: { CDN_STATIC_ASSETS },
} = getConfig();

interface SuccessfulServerSideProps {
  type?: 'success';
  domain?: string;
  carValuationResponse: CarValuationResponse;
  keyInfoDisplayAttributes?: IMetaInfoItem[];
  vehicleMetaTitle: string;
  trim?: string;
  registration: string;
  mileage: string;
  pageName: TCarValuationPageNames;
  make?: string;
  model?: string;
  year?: string;
  bodyType?: string;
  fuelType?: string;
  transmission?: string;
  insuranceRenewalDetails?: InsuranceRenewalDetails;
}

interface ErrorServerSideProps {
  type: 'error';
  errorCode: number;
  domain?: string;
  registration?: string;
  pageName: TCarValuationPageNames;
}

type MyProps = SuccessfulServerSideProps | ErrorServerSideProps;

export type InsuranceRenewalDetails = {
  insuranceRenewalMonth?: string | null;
  currentRegistration?: string | null;
};

function CarValuation(props: MyProps) {
  const { domain = '', pageName } = props;
  const { updateVertical } = useVerticalContext();

  const pageTitle =
    pageName === PAGE.INSTANT_OFFER_VALUATION
      ? 'Instant Offer Valuation - DoneDeal'
      : 'Private Vehicle Valuation - DoneDeal';

  useEffect(() => {
    updateVertical(VERTICAL.MOTOR);
  }, [updateVertical]);

  if (props.type === 'error') {
    return (
      <DefaultLayout domain={domain} headerVariant="TRANSPARENT">
        <NextSeo title={pageTitle} />
        <CarValuationErrorPage
          cdnUrl={CDN_STATIC_ASSETS}
          registration={props.registration}
          pageName={pageName}
        />
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout domain={domain} headerVariant="TRANSPARENT">
      <NextSeo title={pageTitle} />
      <CarValuationPage
        cdnUrl={CDN_STATIC_ASSETS}
        carValuationResponse={props.carValuationResponse}
        keyInfoDisplayAttributes={props.keyInfoDisplayAttributes}
        vehicleMetaTitle={props.vehicleMetaTitle}
        trim={props.trim}
        pageName={pageName}
        registration={props.registration}
        mileage={props.mileage}
        vehicleInfo={{
          make: props.make ?? '',
          model: props.model ?? '',
          year: props.year ?? '',
          fuelType: props.fuelType ?? '',
          transmission: props.transmission ?? '',
          bodyType: props.bodyType ?? '',
        }}
        insuranceRenewalDetails={props.insuranceRenewalDetails}
      />
    </DefaultLayout>
  );
}

export const getServerSideProps: GetServerSideProps<
  SuccessfulServerSideProps | ErrorServerSideProps
> = async (ctx) => {
  const { url } = ctx.req;

  const registration = ctx.query.registration?.toString();
  const mileage = ctx.query.mileage?.toString();

  let pageName: TCarValuationPageNames = PAGE.PRIVATE_VALUATION;

  if (url && url.includes(PAGE.INSTANT_OFFER_VALUATION)) {
    pageName = PAGE.INSTANT_OFFER_VALUATION;
  }

  const session = await getServerSession(ctx.req, ctx.res, nextAuthOptions);

  if (!session) {
    logger.error({
      route: PAGE.VEHICLE_LOOKUP,
      message: 'Session is undefined',
    });

    return {
      props: {
        type: 'error',
        errorCode: 400,
        registration,
        pageName,
      },
    };
  }

  try {
    if (registration && mileage) {
      const vehicleRegData =
        await adWriteApi.refinedVehicleRegistrationNumberLookup(
          registration,
          'cars',
          formatAuthorizationHeader(session.accessToken),
        );

      const carValuationPromise = await carValuationApi.getCarValuation(
        registration,
        mileage,
        pageName,
        formatAuthorizationHeader(session.accessToken),
      );

      const getInsuranceRenewalDetails = async () => {
        try {
          const userResponse = await userRepository.get(
            formatAuthorizationHeader(session.accessToken),
          );
          if (isOk(userResponse)) {
            return {
              insuranceRenewalMonth:
                userResponse.ok.user.vehicle?.insuranceRenewal,
              currentRegistration:
                userResponse.ok.user.vehicle?.registrationNumber,
            };
          } else {
            return {};
          }
        } catch {
          return {};
        }
      };

      const [carValuation, insuranceRenewalDetails] = await Promise.all([
        carValuationPromise,
        getInsuranceRenewalDetails(),
      ]);

      const findMetaInfoBlock = (name: string, hiddenField?: boolean) => {
        if (hiddenField) {
          return vehicleRegData.data?.hiddenFields?.items?.find(
            (item) => item.name === name,
          );
        }

        return vehicleRegData.data?.metaInfoBlock?.items?.find(
          (item) => item.name === name,
        );
      };

      const year = findMetaInfoBlock('year');
      const make = findMetaInfoBlock('make');
      const model = findMetaInfoBlock('model');
      const bodyType = findMetaInfoBlock('bodyType');
      const fuelType = findMetaInfoBlock('fuelType');
      const transmission = findMetaInfoBlock('transmission');

      const trimObj = findMetaInfoBlock('trim', true);
      const trim = trimObj?.value;
      const vehicleMetaTitle = `${year?.value} ${make?.value} ${model?.value}`;

      const keyInfoDisplayAttributes =
        vehicleRegData.data?.metaInfoBlock?.items;

      return {
        props: {
          type: 'success',
          carValuationResponse: carValuation.data,
          keyInfoDisplayAttributes,
          vehicleMetaTitle,
          trim,
          registration,
          mileage,
          pageName,
          make: make?.value,
          model: model?.value,
          year: year?.value,
          bodyType: bodyType?.value,
          fuelType: fuelType?.value,
          transmission: transmission?.value,
          insuranceRenewalDetails,
        },
      };
    } else {
      return {
        props: {
          type: 'error',
          errorCode: 500,
          pageName,
        },
      };
    }
  } catch (error) {
    logger.error({
      route: PAGE.PRIVATE_VALUATION,
      error,
      message: 'Unknown server error',
    });
    const { params } = ctx;
    const registration = params?.registration?.toString();
    return {
      props: {
        type: 'error',
        errorCode: 500,
        registration,
        pageName,
      },
    };
  }
};

export default CarValuation;
